import {
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { GLOSS_CREDITS_ACCOUNT_NAME } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { BalanceForm } from "@bitwarden/web-vault/app/models/types/account.types";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { BookBalance } from "@bitwarden/web-vault/app/shared/utils/helper.account/balance";

export interface StatementAccountWithBalances {
  account: AccountView;
  coreCurrency: string;
  openingBalance: string | BookBalance;
  closingBalance: string | BookBalance;
}

/** A linked account as shown by app-item-display (label + the account it wraps). */
export interface LinkedAccountDisplay {
  label: string;
  templateData: AccountView;
}

@Component({
  selector: "app-select-account-stage",
  templateUrl: "./select-account-stage.component.html",
})
export class SelectAccountStageComponent implements OnInit, OnChanges {
  @Input() statementFromDate: string;
  @Input() statementToDate: string;
  /** Mapped-statement prefill: preselects the parsed account and (if supplied) its balances. */
  @Input() prefill: {
    account: AccountView;
    openingBalance?: string;
    closingBalance?: string;
  } | null = null;
  @Output() accountsSelected = new EventEmitter<StatementAccountWithBalances[]>();

  private userStore = inject(UserStoreService);
  private formBuilder = inject(FormBuilder);
  private injector = inject(Injector);

  accountViews: AccountView[] = [];
  accountNamesList: string[] = [];
  linkedAccounts: AccountView[] = [];
  linkedAccountsDisplay: LinkedAccountDisplay[] = [];
  linkedAccountsWithBalances: StatementAccountWithBalances[] = [];
  accountInAction: AccountView | null = null;
  currentCurrency: string;

  addBalanceFormGroup: FormGroup;

  // Error messages
  noLinkedAccountErrorText: string = "";
  openingBalanceErrorText: string = "";
  closingBalanceErrorText: string = "";

  // Maps for storing balances
  _mOpeningBalances = new Map<string, BookBalance>();
  _mClosingBalances = new Map<string, BookBalance>();

  constructor() {
    this.currentCurrency = this.userStore.preferences.preferenceView()?.coreCurrency;
    this.addBalanceFormGroup = this.formBuilder.group({
      openingBalance: [""],
      closingBalance: [""],
    });
  }

  async ngOnInit(): Promise<void> {
    this.loadAccountViews();
    if (this.prefill?.account) {
      await this.applyPrefill(this.prefill);
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes.prefill || changes.prefill.isFirstChange()) {
      return;
    }

    this.clearLinkedAccounts();
    if (!changes.prefill.currentValue?.account) {
      this.emitAccountsSelected();
      return;
    }

    this.loadAccountViews();
    try {
      await this.applyPrefill(changes.prefill.currentValue);
    } catch {
      this.clearLinkedAccounts();
      this.emitAccountsSelected();
    }
  }

  private loadAccountViews(): void {
    // Exclude the Gloss Credits account — not a user-chooseable statement account.
    this.accountViews = this.userStore.enrichAccounts
      .collection()
      .filter((account) => account.name !== GLOSS_CREDITS_ACCOUNT_NAME);
    this.updateAccountNames();
  }

  private clearLinkedAccounts(): void {
    this.linkedAccounts = [];
    this.linkedAccountsDisplay = [];
    this.linkedAccountsWithBalances = [];
    this._mOpeningBalances.clear();
    this._mClosingBalances.clear();
    this.resetAccountSelection();
  }

  /**
   * Preselect the statement's account. When the statement also supplied opening + closing
   * balances we auto-link it (reusing linkSelectedAccount so balances are built the same way);
   * otherwise we just select it and leave the balances for the user to enter.
   */
  private async applyPrefill(prefill: {
    account: AccountView;
    openingBalance?: string;
    closingBalance?: string;
  }): Promise<void> {
    // Use the enriched instance from accountViews so the dropdown/disabled logic matches.
    const account = this.accountViews.find((a) => a.id === prefill.account.id);
    if (!account || account.origin !== Origin.manual) {
      return;
    }
    this.accountInAction = account;
    this.currentCurrency = account.currency;
    this.addBalanceFormGroup.patchValue({
      openingBalance: prefill.openingBalance ?? "",
      closingBalance: prefill.closingBalance ?? "",
    });
    if (
      !this.isBalanceEmpty(prefill.openingBalance) &&
      !this.isBalanceEmpty(prefill.closingBalance)
    ) {
      await this.linkSelectedAccount();
    }
  }

  private updateAccountNames(): void {
    this.accountNamesList = this.accountViews.map((account) => account.name);
  }

  get disabledOptions(): string[] {
    return [
      ...this.linkedAccounts.map((linkedAccount) => linkedAccount.name),
      ...this.accountViews
        .filter((accountView) => accountView.origin !== Origin.manual)
        .map((accountView) => accountView.name),
    ];
  }

  accountSelected(selectedAccountName: string): void {
    const selectedAccount = this.accountViews.find(
      (account) => account.name === selectedAccountName,
    );
    if (selectedAccount) {
      this.accountInAction = selectedAccount;
      this.currentCurrency = selectedAccount.currency;
      this.resetBalanceInputs();
    }
  }

  openingBalanceChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.addBalanceFormGroup.get("openingBalance")?.setValue(value);
    this.openingBalanceErrorText = "";
  }

  closingBalanceChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.addBalanceFormGroup.get("closingBalance")?.setValue(value);
    this.closingBalanceErrorText = "";
  }

  resetBalanceInputs(): void {
    this.addBalanceFormGroup.reset();
    this.openingBalanceErrorText = "";
    this.closingBalanceErrorText = "";
  }

  resetAccountSelection(): void {
    this.accountInAction = null;
    this.resetBalanceInputs();
    this.noLinkedAccountErrorText = "";
  }

  async linkSelectedAccount(): Promise<void> {
    if (!this.validateLinkAccountInputs()) {
      return;
    }

    const isLinked = this.linkedAccounts.some((account) => account.id === this.accountInAction.id);
    if (!isLinked) {
      this.linkedAccounts.push(this.accountInAction);
      this.linkedAccountsDisplay.push({
        label: `${this.accountInAction.name}`,
        templateData: this.accountInAction,
      });
    }

    await this.setBalanceForm();
    this.addDataIntoLinkedAccountsWithBalances(this.accountInAction);
    this.resetAccountSelection();

    // Emit the updated accounts
    this.emitAccountsSelected();
  }

  private validateLinkAccountInputs(): boolean {
    if (!this.accountInAction) {
      this.noLinkedAccountErrorText = "inputRequired";
      return false;
    }

    const openingBalance = this.addBalanceFormGroup.value.openingBalance;
    const closingBalance = this.addBalanceFormGroup.value.closingBalance;

    if (this.isBalanceEmpty(openingBalance)) {
      this.openingBalanceErrorText = "inputRequired";
    }
    if (this.isBalanceEmpty(closingBalance)) {
      this.closingBalanceErrorText = "inputRequired";
    }

    if (this.isBalanceEmpty(openingBalance) || this.isBalanceEmpty(closingBalance)) {
      return false;
    }

    return true;
  }

  /** A balance is "missing" only when blank/null - 0 is a valid balance. */
  private isBalanceEmpty(value: unknown): boolean {
    return value === null || value === undefined || value === "";
  }

  private addDataIntoLinkedAccountsWithBalances(account: AccountView): void {
    const itemIndex = this.linkedAccountsWithBalances.findIndex(
      (acc) => acc.account.id === account.id,
    );
    const linkedAccountData: StatementAccountWithBalances = {
      account: this.accountInAction,
      coreCurrency: this.currentCurrency,
      openingBalance: this._mOpeningBalances.get(this.accountInAction.id),
      closingBalance: this._mClosingBalances.get(this.accountInAction.id),
    };
    if (itemIndex >= 0) {
      this.linkedAccountsWithBalances[itemIndex] = linkedAccountData;
    } else {
      this.linkedAccountsWithBalances.push(linkedAccountData);
    }
  }

  private async setBalanceForm(): Promise<void> {
    const openingBalance = this.addBalanceFormGroup.value.openingBalance;
    const closingBalance = this.addBalanceFormGroup.value.closingBalance;

    const openingBalanceForm: BalanceForm = {
      balance: openingBalance,
      currency: this.currentCurrency,
      date: this.statementFromDate,
      transactionStatus: TransactionStatusEnum.opening,
      type: "Opening",
    };

    const closingBalanceForm: BalanceForm = {
      balance: closingBalance,
      currency: this.currentCurrency,
      date: this.statementToDate,
      transactionStatus: TransactionStatusEnum.closing,
      type: "Closing",
    };

    const openingBalanceSaveData = new BookBalance(
      this.accountInAction,
      openingBalanceForm,
      this.injector,
    );

    const closingBalanceSaveData = new BookBalance(
      this.accountInAction,
      closingBalanceForm,
      this.injector,
    );

    this._mOpeningBalances.set(this.accountInAction.id, openingBalanceSaveData);
    this._mClosingBalances.set(this.accountInAction.id, closingBalanceSaveData);
  }

  receivedEditAccount(account: AccountView): void {
    this.accountInAction = account;
    this.currentCurrency = account.currency;

    // Pre-fill the form with existing values (?? so a real 0 balance isn't blanked out)
    const openingBalance = this._mOpeningBalances.get(account.id)?.balanceForm?.balance ?? "";
    const closingBalance = this._mClosingBalances.get(account.id)?.balanceForm?.balance ?? "";

    this.addBalanceFormGroup.patchValue({
      openingBalance: openingBalance,
      closingBalance: closingBalance,
    });
  }

  receivedRemoveAccount(account: AccountView): void {
    // Remove from linked accounts
    this.linkedAccounts = this.linkedAccounts.filter((acc) => acc.id !== account.id);
    this.linkedAccountsDisplay = this.linkedAccountsDisplay.filter(
      (acc) => acc.templateData.id !== account.id,
    );

    // Remove from balances
    this._mOpeningBalances.delete(account.id);
    this._mClosingBalances.delete(account.id);

    // Remove from linked accounts with balances
    this.linkedAccountsWithBalances = this.linkedAccountsWithBalances.filter(
      (acc) => acc.account.id !== account.id,
    );

    // Emit the updated accounts
    this.emitAccountsSelected();
  }

  private emitAccountsSelected(): void {
    this.accountsSelected.emit(this.linkedAccountsWithBalances);
  }

  /**
   * Called by the wizard before leaving this stage. First commits any in-progress
   * account the user filled in but didn't explicitly "Link" (so they aren't sent to
   * an empty next stage by mistake), then reports whether at least one account is
   * linked. Surfaces inline errors and returns false when it isn't safe to proceed.
   */
  async ensureLinkedAccountsForNext(): Promise<boolean> {
    // Auto-commit a selection the user filled in but didn't click "Link Account" on.
    // Only when they actually entered a balance, or there are no linked accounts yet -
    // otherwise an untouched dropdown selection would block a statement that already has
    // valid linked accounts. linkSelectedAccount() clears accountInAction only on success;
    // if it's still set the inputs were invalid and the per-field errors are now showing.
    const opening = this.addBalanceFormGroup.value.openingBalance;
    const closing = this.addBalanceFormGroup.value.closingBalance;
    const hasPendingInput = !this.isBalanceEmpty(opening) || !this.isBalanceEmpty(closing);

    if (this.accountInAction && (hasPendingInput || this.linkedAccountsWithBalances.length === 0)) {
      await this.linkSelectedAccount();
      if (this.accountInAction) {
        return false;
      }
    }

    if (this.linkedAccountsWithBalances.length === 0) {
      this.noLinkedAccountErrorText = "linkAtLeastOneAccount";
      return false;
    }

    this.noLinkedAccountErrorText = "";
    return true;
  }
}
